import { EventEmitter } from '@spotify-internal/emitter';
import { logging } from '@js-sdk/debug-tools';
import { Event as InternalEvent } from '../../enums/event';
import { Errors } from '../../enums/errors';
import { KeySystem } from '../../enums/key_system';
import { EMEError } from '../../error/eme';
import { CappingError } from '../../error/capping';
import { defer } from '../defer';
import { install as installPolyfill } from './polyfills';
import { KeySystemSetting } from './keysystem_setting';
import { EMEConfigs } from './configs';
import { getMediaKeySystemConfiguration } from '../helpers/eme';
const CODEC_EXP = /([^;]+)(?:;\s?codecs="(.*)")?/;
const debugLogger = logging.forTag('playback.eme_manager');
const LabelString = {
    EMPTY: '-no-robustness',
    SW_CRYPTO: '-sw-crypto',
    SW_DECODE: '-sw-decode',
    HW_CRYPTO: '-hw-crypto',
    HW_DECODE: '-hw-decode',
    HW_ALL: '-hw-all',
};
/**
 * The base url for unauthenticated certificate requests.
 */
const UNAUTHENTICATED_CERT_BASE = 'https://spclient.wg.spotify.com';
function createConfigFilter(disabledConfigs) {
    const filters = [
        // This is a default value that we add so that in the event that there are
        // no disabled configs, none of the configs will be able to match this one.
        '--ensure-no-match--',
    ];
    if (disabledConfigs && disabledConfigs.length) {
        let len = disabledConfigs.length;
        while (len--) {
            if (!LabelString.hasOwnProperty(disabledConfigs[len])) {
                continue;
            }
            filters.push(LabelString[disabledConfigs[len]]);
        }
    }
    return new RegExp(`(${filters.join('|')})$`);
}
/**
 * Manages the KeySessions for a player tag.
 */
export class EMEManager extends EventEmitter {
    constructor(options) {
        var _a;
        super();
        /**
         * The base URL for the unauthenticated server certificate request.
         */
        this._unauthServerCertificateBase = '';
        /**
         * A promise that will be resolved with the system's KeyAccess object.
         */
        this._keySystemDeferred = defer();
        /**
         * A promise that will be resolved with the KeySystemInfo.
         */
        this._configuration = defer();
        /**
         * Settings for the keysystem
         */
        this._keySystemSettings = null;
        /**
         * Keeps track of all current KeySessions.
         */
        this._keySessions = [];
        this._configs = [];
        /**
         * A promise that indicates the previous request to get the server
         * certificate.
         */
        this._pendingCertificateRequest = null;
        /**
         * The current server certificate.
         */
        this._certificate = null;
        this._transport = options.transport;
        this._emeImpl = options.emeImpl;
        this._noServerCertificate = !!options.noServerCertificate;
        this._precacheServerCertificate = (_a = options.precacheServerCertificate) !== null && _a !== void 0 ? _a : true;
        this._unauthServerCertificateBase =
            options.unauthServerCertificateBase || UNAUTHENTICATED_CERT_BASE;
        this._configFilter = createConfigFilter(options.disallowRobustnessValues || []);
        this._preferredKeySystems = options.preferredKeySystems || [
            KeySystem.WIDEVINE,
            KeySystem.PLAYREADY,
            KeySystem.PLAYREADY_HARDWARE,
            KeySystem.FAIRPLAY,
        ];
    }
    /**
     * Creates a new instance of the EMEManager
  
     * @param options - The options for the instance.
     * @return The new EMEManager instance.
     */
    static create(options) {
        return new Promise((resolve) => {
            const emeImpl = installPolyfill();
            if (!emeImpl) {
                throw EMEError.fatal(Errors.EME_API_NOT_SUPPORTED, 'Platform does not support navigator.requestMediaKeySystemAccess');
            }
            resolve(new EMEManager(Object.assign(Object.assign({}, options), { emeImpl })).init());
        });
    }
    /**
     * Prepares the configuration object.
     *
     * @param keySystemAccess - The keysystem from requestMediaKeySystemAccess.
     * @return The key system info.
     */
    _prepareConfiguration(keySystemAccess) {
        const config = keySystemAccess.getConfiguration();
        debugLogger.info('KeySystem configured as ', config.label);
        if (!config.audioCapabilities || !config.audioCapabilities.length) {
            // MS Edge does not correctly set the audio and video capabilities, but we
            // need to add them to correctly determine whether we can play video.
            this._appendProbableMediaCapabilities(keySystemAccess.keySystem, config);
        }
        const parsed = {
            keySystem: keySystemAccess.keySystem,
            keySystemImpl: this._emeImpl,
            audioFormats: this._parseCapabilities(config.audioCapabilities),
            videoFormats: this._parseCapabilities(config.videoCapabilities),
        };
        // FIXME Add a check for keySystemAccess.keySystem.
        this._keySystemSettings =
            KeySystemSetting[keySystemAccess.keySystem];
        this._configuration.resolve(parsed);
        return parsed;
    }
    /**
     * Parses the KeySystemAccess capabilities.
     *
     * @param capabilities - The capabilities to parse.
     * @return The formats available from the capabilities.
     */
    _parseCapabilities(capabilities) {
        if (!capabilities) {
            return [];
        }
        const results = [];
        for (let i = 0, l = capabilities.length; i < l; i++) {
            const cap = capabilities[i];
            if (!cap || !cap.contentType) {
                continue;
            }
            const matches = cap.contentType.match(CODEC_EXP);
            if (!matches) {
                continue;
            }
            results.push({
                contentType: cap.contentType,
                mimeType: matches[1],
                codec: matches[2],
            });
        }
        return results;
    }
    /**
     * Appends the audio and video configurations to a KeySystemConfiguration
     * object.
     *
     * @param ks - The keysystem name.
     * @param origConfig - The KeySystemConfiguration.
     */
    _appendProbableMediaCapabilities(ks, origConfig) {
        const configuration = getMediaKeySystemConfiguration(this._configs, (contentType) => {
            if (contentType.indexOf('webm') !== -1 &&
                (ks === KeySystem.PLAYREADY || ks === KeySystem.PLAYREADY_HARDWARE)) {
                // Edge will report that it supports WebM, but testing shows that it
                // cannot, so we skip WebM on all PlayReady platforms for now.
                return false;
            }
            if (MediaSource.isTypeSupported(contentType)) {
                return true;
            }
            return false;
        });
        if (configuration) {
            origConfig.audioCapabilities = configuration.audioCapabilities;
            origConfig.videoCapabilities = configuration.videoCapabilities;
        }
    }
    /**
     * Try to set the server certificate for a MediaKeys object.
     *
     * Note that failure to set the certificate is not considered an error, which is
     * why we warn than fail.
     *
     * @param url - The license server URL.
     * @param mediaKeys - The MediaKey object whose certificate is being
     *     set.
     * @return A promise that will be resolved to the current EMEManager instance.
     */
    _trySetServerCertificate(url, mediaKeys) {
        const cachedCertificate = this._certificate;
        const certificatePromise = cachedCertificate && cachedCertificate.expiry > Date.now()
            ? Promise.resolve(cachedCertificate)
            : this._requestServerCertificate(url);
        return certificatePromise
            .then((certificate) => mediaKeys.setServerCertificate(certificate.contents))
            .then(() => this)
            .catch((e) => {
            debugLogger.warn('Error from setting server certificate', e);
            return this;
        });
    }
    _cacheServerCertificate(keysystem) {
        let licenseServer = '';
        switch (keysystem) {
            case KeySystem.FAIRPLAY:
                licenseServer = 'fairplay';
                break;
            case KeySystem.WIDEVINE:
                licenseServer = 'widevine';
                break;
            default:
                // Others are not supported.
                return Promise.resolve(this);
        }
        return this._requestServerCertificate(`${this._unauthServerCertificateBase}/${licenseServer}-license`, false).then(() => this, (e) => {
            debugLogger.warn('Cannot precache server certificate', e);
            return this;
        });
    }
    _requestServerCertificate(url, authorize = true) {
        if (this._pendingCertificateRequest) {
            return this._pendingCertificateRequest;
        }
        const certificateRequest = this._transport
            .request(`${url}/v1/application-certificate`, {
            authorize: authorize,
            responseType: 'arraybuffer',
            parseResponseHeaders: true,
            retry: {
                condition: (response, StatusFamily) => response.getStatusFamily() === StatusFamily.SERVER_ERROR,
            },
        })
            .then((response) => {
            var _a;
            if (!response.body) {
                throw new TypeError('Response body is invalid');
            }
            this._pendingCertificateRequest = null;
            let ttl = 3600; // One hour
            const header = (_a = response.headers) === null || _a === void 0 ? void 0 : _a.get('cache-control');
            if (header) {
                const matches = header.match(/(?:^|,|\s)max-age=(\d+)(?:,|$)/);
                if (matches && matches[1]) {
                    ttl = parseInt(matches[1], 10) * 1000;
                }
            }
            this._certificate = {
                expiry: Date.now() + (ttl - 600),
                contents: response.body,
            };
            return this._certificate;
        });
        return certificateRequest;
    }
    _extractPlayReadyChallenge(message, playId) {
        let challenge = null;
        const charCodeNumber = new Uint16Array(message);
        const xmlString = String.fromCharCode.apply(null, charCodeNumber);
        if (xmlString.indexOf('PlayReadyKeyMessage') === -1) {
            // This check will succeed in platforms with older PlayReady versions where
            // the message is already extracted. In these platforms, we notice that the
            // message is actually UTF-8 instead of UTF-16, which leads to a garbled
            // xmlString when we try to parse it.
            //
            // When we encounter this, we should simply return the old message and use
            // that directly.
            debugLogger.info('Using unextracted PlayReady message.');
            return message;
        }
        const keyMessageXml = new DOMParser().parseFromString(xmlString, 'application/xml');
        const firstNode = keyMessageXml.getElementsByTagName('Challenge')[0];
        const firstChildNode = firstNode.childNodes[0];
        const nodeValue = firstChildNode.nodeValue;
        if (nodeValue) {
            try {
                challenge = atob(nodeValue);
                const headerNames = keyMessageXml.getElementsByTagName('name');
                const headerValues = keyMessageXml.getElementsByTagName('value');
                if (headerNames.length !== headerValues.length) {
                    throw new EMEError(Errors.EME_HEADER_KEY_VALUE_MISMATCH, 'Mismatched header <name>/<value> pair in key message');
                }
            }
            catch (e) {
                // An error can be thrown either from the extraction of the data from the
                // XML or when the header <name>/<value> pair don't match
                this.emit(InternalEvent.EME_LICENSE_REQUEST_ERROR, {
                    playId: playId,
                    error: e,
                });
                // Make sure challenge is null in case of error
                challenge = null;
            }
        }
        else {
            this.emit(InternalEvent.EME_LICENSE_REQUEST_ERROR, {
                playId: playId,
                error: new EMEError(Errors.EME_HEADER_KEY_VALUE_MISMATCH, 'Mismatched header <name>/<value> pair in key message'),
            });
        }
        return challenge;
    }
    /**
     * Handles a message event from a created KeySession.
     *
     * @param options - The options contain the following properties:
     * @property keySystem The name of the KeySystem.
     * @property keySession The KeySession.
     * @property licenseServer The license server URL for the file.
     * @property {module:spotify-promise-plus.Deferred} playId The deferred promise // <= TODO is this true ?
     *     created from createSession associated with this message event.
     * @param event - The message event from the KeySession.
     */
    _onMessage(options, event // TODO Confirm
    ) {
        options.timeMap.generate.end = Date.now();
        // TODO Confirm
        let message = 'message' in event ? event.message : null;
        const messageType = 'messageType' in event ? event.messageType : undefined;
        debugLogger.info('Got EME message event with type', messageType);
        if (message && options.keySystem === KeySystem.PLAYREADY) {
            message = this._extractPlayReadyChallenge(message, options.playId);
        }
        if (!message) {
            debugLogger.info('Ignoring message event: no valid request payload');
            return;
        }
        if (!options.licenseServer) {
            debugLogger.info('Ignoring message event: no licenseServer url');
            return;
        }
        options.timeMap.request.start = Date.now();
        let licenseServer = options.licenseServer;
        if (options.keySystem === KeySystem.FAIRPLAY) {
            licenseServer += '&assetId=hex';
        }
        this._transport
            .request(licenseServer, {
            method: 'POST',
            payload: message,
            responseType: 'arraybuffer',
            retry: {
                condition: (response, StatusFamily) => {
                    if (response.status === 400 ||
                        response.status === 402 ||
                        response.status === 403) {
                        // These are known errors that aren't recoverable, no need to retry.
                        return false;
                    }
                    return (!response.body ||
                        response.getStatusFamily() !== StatusFamily.SUCCESS);
                },
            },
        })
            .then(this._handleLicenseResponse.bind(this, options));
    }
    /**
     * Handles the response of the license request.
     *
     * @param options - The request options.
     * @param response - The server's response.
     */
    _handleLicenseResponse(options, response) {
        const emitError = (error) => {
            error.playId = options.playId;
            if (options.licenseServer) {
                error.licenseServer = options.licenseServer;
            }
            this.emit(InternalEvent.EME_LICENSE_REQUEST_ERROR, {
                playId: options.playId,
                error: error,
            });
        };
        options.timeMap.request.end = Date.now();
        let error;
        if (response.status === 200) {
            if (!response.body) {
                error = new EMEError(Errors.EME_LICENSE_REQUEST_EMPTY_RESPONSE, 'Empty license response body');
                error.status = response.status;
                emitError(error);
                return;
            }
            try {
                options.timeMap.update.start = Date.now();
                options.keySession.update(response.body).catch((e) => {
                    emitError(new EMEError(Errors.EME_LICENSE_UPDATE_FAILED, e.message || 'License update failed.'));
                });
                debugLogger.info('KeySession updated.');
            }
            catch (e) {
                emitError(new EMEError(Errors.EME_LICENSE_UPDATE_FAILED, e.message || 'License update failed.'));
            }
            return;
        }
        // Since the response type should be array buffer for successful
        // operations, we need to parse this into JSON when dealing with errors.
        let errMsg;
        try {
            if (response.body) {
                const charCodeNumber = new Uint8Array(response.body);
                const maybeJSON = String.fromCharCode.apply(String, charCodeNumber);
                errMsg = JSON.parse(maybeJSON).errorMsg;
            }
        }
        catch (_a) {
            // If the body isn't valid JSON, we ignore it.
        }
        if (response.status === 402) {
            // The current user is capped.
            this.emit(InternalEvent.EME_LICENSE_REQUEST_CAPPED, {
                playId: options.playId,
                error: new CappingError(Errors.CAPPING_USER_IS_CAPPED, 'User is capped.'),
            });
        }
        else if (response.status === 400 && errMsg === 'widevine error') {
            error = EMEError.fatal(Errors.EME_LICENSE_REQUEST_WIDEVINE_ERROR, 'Widevine license server failed to parse request.');
            error.status = response.status;
            error.shouldRefreshEndpoint = true;
            emitError(error);
        }
        else {
            error = new EMEError(Errors.EME_LICENSE_REQUEST_FAILED_WITH_STATUS, `License request failed (${errMsg || 'unknown'})`);
            error.status = response.status;
            error.shouldRefreshEndpoint =
                response.status === 400 || response.status === 403;
            emitError(error);
        }
    }
    _selectKeySystem(ksAccessMap) {
        const preferredKeySystems = this._preferredKeySystems;
        for (let i = 0, l = preferredKeySystems.length; i < l; i++) {
            const ksName = preferredKeySystems[i];
            if (ksName in ksAccessMap) {
                return ksAccessMap[ksName];
            }
        }
        return null;
    }
    _testMediaKeys(keySystemAccess) {
        return new Promise((resolve) => {
            resolve(keySystemAccess.createMediaKeys());
        }).then((mediaKeys) => {
            if (!mediaKeys) {
                return Promise.reject(EMEError.fatal(Errors.EME_MEDIA_KEYS_NOT_SUPPORTED, 'Cannot create MediaKeys from KeySystemAccess'));
            }
            // The FairPlay polyfill requires that the server certificate be set
            // prior to creating a session. Since this is not a real session, we
            // set the certificate to an empty array buffer for the time being.
            if (keySystemAccess.keySystem === KeySystem.FAIRPLAY) {
                mediaKeys.setServerCertificate(new ArrayBuffer(0));
            }
            try {
                const keySession = mediaKeys.createSession();
                if (!keySession) {
                    throw new Error('');
                }
            }
            catch (e) {
                return Promise.reject(EMEError.fatal(Errors.EME_MEDIA_KEY_SESSION_NOT_SUPPORTED, e.message || 'Cannot create MediaKeySession from KeySystemAccess'));
            }
            return Promise.resolve(keySystemAccess);
        }, (e) => {
            debugLogger.error(e.name);
            return Promise.reject(EMEError.fatal(Errors.EME_MEDIA_KEYS_NOT_SUPPORTED, e.message || 'Unknown error'));
        });
    }
    /**
     * Saves a new KeySession and removes the oldest KeySession.
     *
     * @param keySessionData - The KeySession to add.
     */
    _saveSession(keySessionData) {
        this._keySessions.push(keySessionData);
    }
    destroySessions() {
        const promises = [];
        const keySessions = this._keySessions;
        for (let i = 0, l = keySessions.length; i < l; i++) {
            try {
                const keySessionData = keySessions[i];
                const keySession = keySessionData.keySession;
                if (!keySession) {
                    continue;
                }
                if (keySessionData.onMessageListener) {
                    keySession.removeEventListener(InternalEvent.KEY_SESSION_MESSAGE, keySessionData.onMessageListener);
                }
                if (keySessionData.onKeyStatusChangeListener) {
                    keySession.removeEventListener(InternalEvent.KEY_SESSION_STATUSES_CHANGE, keySessionData.onKeyStatusChangeListener);
                }
                const stopPromise = Promise.resolve(keySession.close()).catch((e) => {
                    debugLogger.warn('Failed to close KeySession', e);
                });
                promises.push(stopPromise);
                // Explicitly null.
                keySessionData.keySession = null;
                keySessionData.onMessageListener = null;
                keySessionData.onKeyStatusChangeListener = null;
                debugLogger.info('Closed KeySession');
            }
            catch (e) {
                debugLogger.warn('Failed to close KeySession', e);
            }
        }
        this._keySessions = [];
        return Promise.all(promises);
    }
    /**
     * Initializes the EMEManager instance.
     *
     * @return The EMEManager instance.
     */
    init() {
        const keySystemDeferred = this._keySystemDeferred;
        const ksAccessMap = {};
        const promises = [];
        const configs = [];
        for (let i = 0, l = EMEConfigs.length; i < l; i++) {
            const config = EMEConfigs[i];
            if (config.label && this._configFilter.test(config.label)) {
                continue;
            }
            configs.push(config);
        }
        this._configs = configs;
        for (const ksName in KeySystem) {
            // debug_enum prevents us from calling hasOwnProperty directly.
            if (!Object.prototype.hasOwnProperty.call(KeySystem, ksName)) {
                continue;
            }
            const name = ksName;
            promises.push(navigator
                .requestMediaKeySystemAccess(KeySystem[name], configs)
                .then((keySystemAccess) => {
                ksAccessMap[keySystemAccess.keySystem] = keySystemAccess;
            })
                .catch(() => {
                // Do nothing.
            }));
        }
        return Promise.all(promises)
            .then(this._selectKeySystem.bind(this, ksAccessMap))
            .then((keySystemAccess) => {
            if (!keySystemAccess) {
                return Promise.reject(new EMEError(Errors.EME_NO_SUPPORTED_KEYSYSTEM, 'No supported keysystem was found.'));
            }
            return this._testMediaKeys(keySystemAccess);
        })
            .then((keySystem) => {
            this._prepareConfiguration(keySystem);
            keySystemDeferred.resolve(keySystem);
            return this._precacheServerCertificate
                ? this._cacheServerCertificate(keySystem.keySystem)
                : this;
        })
            .catch((e) => {
            const code = e.code || Errors.EME_NO_SUPPORTED_KEYSYSTEM;
            const message = e.message || 'No supported keysystem';
            keySystemDeferred.reject(EMEError.fatal(code, message));
            return keySystemDeferred.promise.then(() => this);
        });
    }
    /**
     * Creates a MediaKeys object and attaches it to a HTMLMediaElement.
     *
     * @param player - The object that will receive the created MediaKeys object.
     * @return A promise that will be resolved to the current EMEManager instance.
     */
    createMediaKeys(player) {
        return this._keySystemDeferred.promise
            .then((keySystem) => keySystem.createMediaKeys())
            .then((mediaKeys) => {
            // FIXME: Remove Promise.all([]) and just return player.mediaKeys in chain.
            return Promise.all([player.setMediaKeys(mediaKeys), mediaKeys]);
        })
            .then(([_result, mediaKeys]) => {
            if (!player.mediaKeys) {
                throw new EMEError(Errors.EME_PLAYER_MEDIA_KEYS_SETTING_FAILED, 'Failed to set MediaKeys on HTMLMediaElement');
            }
            return this._configuration.promise.then(() => {
                const settings = this._keySystemSettings;
                if (!settings ||
                    this._noServerCertificate ||
                    !settings.withCertificate) {
                    return this;
                }
                return this._trySetServerCertificate(settings.licenseServer, mediaKeys);
            });
        });
    }
    /**
     * Removes the media keys from an element.
     *
     * @param player - The MediaElement whose MediaKeys will be removed.
     * @return A promise that will be resolved to the current EMEManager instance.
     */
    removeMediaKeys(player) {
        return Promise.resolve(player.setMediaKeys(null)).then(() => this);
    }
    /**
     * Returns the KeySystemInfo.
     *
     * @return The key system info.
     */
    getKeySystemInfo() {
        return this._configuration.promise;
    }
    /**
     * Returns the "name" of the EME implementation for the platform.
     *
     * Note that unlike most other methods, this method will return the value
     * synchronously.
     *
     * @return The name of the EME implementation.
     * Refer to the polyfills file for possible values.
     */
    getKeySystemImpl() {
        return this._emeImpl;
    }
    /**
     * Creates a new KeySession with a params object.
     *
     * @param params - The parameters for this KeySession.
     * @return True if the KeySession was successfully created.
     */
    createSessionWithParams(params) {
        return new Promise((resolve, reject) => {
            debugLogger.info('Creating KeySession', params.keySystem);
            const keySession = params.mediaKeys.createSession();
            // Internal map of timestamps that we will use to compute the different
            // operations. The map needs to be updated at the start of the operation.
            const timeMap = {
                generate: { start: 0, end: 0 },
                request: { start: 0, end: 0 },
                update: { start: 0, end: 0 },
            };
            const licenseServer = (this._keySystemSettings && this._keySystemSettings.licenseServer) ||
                undefined;
            const onMessageListener = this._onMessage.bind(this, {
                keySystem: params.keySystem,
                keySession: keySession,
                licenseServer: params.licenseServer || licenseServer,
                playId: params.playId,
                timeMap: timeMap,
            });
            keySession.addEventListener(InternalEvent.KEY_SESSION_MESSAGE, onMessageListener);
            const onKeyStatusChangeListener = () => {
                debugLogger.info('KeyStatus change');
                timeMap.update.end = Date.now();
                // Return elapsed for each operation.
                resolve({
                    elapsed: {
                        generate: Math.max(timeMap.generate.end - timeMap.generate.start, 0),
                        request: Math.max(timeMap.request.end - timeMap.request.start, 0),
                        update: Math.max(timeMap.update.end - timeMap.update.start, 0),
                    },
                });
            };
            keySession.addEventListener(InternalEvent.KEY_SESSION_STATUSES_CHANGE, onKeyStatusChangeListener);
            this._saveSession({
                keySession: keySession,
                onMessageListener: onMessageListener,
                onKeyStatusChangeListener: onKeyStatusChangeListener,
            });
            debugLogger.info('Generating KeySession request', params.keySystem);
            timeMap.generate.start = Date.now();
            return Promise.resolve(keySession.generateRequest(params.initDataType, params.initData.buffer)).catch((e) => {
                if (!e) {
                    return;
                }
                let type;
                switch (e.name) {
                    case 'NotSupportedError':
                        type = Errors.EME_NOT_SUPPORTED_ERROR;
                        break;
                    case 'InvalidStateError':
                        type = Errors.EME_INVALID_STATE_ERROR;
                        break;
                    default:
                        type = Errors.EME_UNKNOWN_ERROR;
                }
                reject(new EMEError(type, e.message || 'Unknown error message.'));
            });
        });
    }
}
//# sourceMappingURL=manager.js.map